import React from "react";
import {Controller, useFormContext} from "react-hook-form";
import {Box, Colors, Container, Text} from "@bounce/shared/ui";
import {MenuItem, Select, TextField} from "../../../ui/components";
import {SegmentPageKind} from "../../../apis/requests/segments/types";
import {
  DEFAULT_SEGMENT_TYPE,
  MARKETING_SEGMENT_TYPES,
  CLEAR_OPTION_LABEL,
  NO_SEGMENT_CATEGORY,
  OPTIONAL_PLACEHOLDER,
  SEGMENT_CATEGORIES,
  SEGMENT_CATEGORY_DESCRIPTION,
  SEGMENT_CATEGORY_INFO,
  SEGMENT_TYPE_INFO,
} from "./constants";
import {FormValues} from "./schema";

type Props = {
  pageKind: SegmentPageKind;
  isCategoryFallback: boolean;
};

const DetailsCard = ({pageKind, isCategoryFallback}: Props) => {
  const {control, watch} = useFormContext<FormValues>();
  const segmentType = watch("type");

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: Colors.tertiary._100,
        borderRadius: "12px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Text variant="interDisplay600" size={16} sx={{color: Colors.grey._800}}>
        Details
      </Text>
      <Container row fullWidth sx={{gap: 2}}>
        <Box sx={{flex: 1}}>
          <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
            Name <span style={{color: Colors.red._700}}>*</span>
          </Text>
          <Controller
            name="name"
            control={control}
            render={({field: {ref, ...field}, fieldState}) => (
              <TextField
                {...field}
                inputRef={ref}
                fullWidth
                size="small"
                placeholder="Segment name"
                error={!!fieldState.error}
                sx={{backgroundColor: Colors.bw.white, borderRadius: "8px"}}
              />
            )}
          />
        </Box>
        <Box sx={{flex: 1}}>
          <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
            Description
          </Text>
          <Controller
            name="description"
            control={control}
            render={({field: {ref, ...field}}) => (
              <TextField
                {...field}
                inputRef={ref}
                fullWidth
                size="small"
                placeholder="Optional. Shown in the segments list."
                sx={{backgroundColor: Colors.bw.white, borderRadius: "8px"}}
              />
            )}
          />
        </Box>
      </Container>
      {pageKind === "marketing" && (
        <Box>
          <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
            Type <span style={{color: Colors.red._700}}>*</span>
          </Text>
          <Container row fullWidth sx={{gap: 2, alignItems: "center", flexWrap: "nowrap"}}>
            <Box sx={{flex: 1}}>
              <Controller
                name="type"
                control={control}
                render={({field}) => (
                  <Select
                    {...field}
                    fullWidth
                    size="small"
                    disabled={isCategoryFallback}
                    sx={{backgroundColor: Colors.bw.white, borderRadius: "8px"}}
                  >
                    {MARKETING_SEGMENT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {SEGMENT_TYPE_INFO[type].label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>
            <Text size={12} sx={{flex: 1, color: Colors.grey._600}}>
              {SEGMENT_TYPE_INFO[segmentType].description}
            </Text>
          </Container>
        </Box>
      )}
      {pageKind === "marketing" && segmentType === DEFAULT_SEGMENT_TYPE && (
        <Box>
          <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
            Category
          </Text>
          <Container row fullWidth sx={{gap: 2, alignItems: "center", flexWrap: "nowrap"}}>
            <Box sx={{flex: 1}}>
              <Controller
                name="segment_category"
                control={control}
                render={({field}) => (
                  <Select
                    {...field}
                    fullWidth
                    size="small"
                    displayEmpty
                    disabled={isCategoryFallback}
                    renderValue={() =>
                      field.value ? (
                        SEGMENT_CATEGORY_INFO[field.value].label
                      ) : (
                        <Text size={14} sx={{color: Colors.grey._400}}>
                          {OPTIONAL_PLACEHOLDER}
                        </Text>
                      )
                    }
                    sx={{backgroundColor: Colors.bw.white, borderRadius: "8px"}}
                  >
                    <MenuItem value={NO_SEGMENT_CATEGORY}>
                      <Text size={14} sx={{color: Colors.grey._400}}>
                        {CLEAR_OPTION_LABEL}
                      </Text>
                    </MenuItem>
                    {SEGMENT_CATEGORIES.map((segmentCategory) => (
                      <MenuItem key={segmentCategory} value={segmentCategory}>
                        {SEGMENT_CATEGORY_INFO[segmentCategory].label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>
            <Text size={12} sx={{flex: 1, color: Colors.grey._600}}>
              {SEGMENT_CATEGORY_DESCRIPTION}
            </Text>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default DetailsCard;
